import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DealOptions, DealSummary, PipelineStage } from '@agency/contracts';
import { Badge, Button, Drawer, SelectField, Skeleton, TextField, useToast } from '@agency/ui';
import { ApiClientError, get, patch } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate } from '../../lib/format.ts';
import { ConflictNotice, QueryError } from '../../lib/query-state.tsx';
import { RecordWorkPanel } from './crm-work.tsx';

export function DealDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [stageId, setStageId] = useState('');
  const [probability, setProbability] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const [marketCode, setMarketCode] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);

  const detail = useQuery<DealSummary, ApiClientError>({ queryKey: ['deal', id], queryFn: () => get(`/deals/${id}`), enabled: !!id });
  const stages = useQuery<PipelineStage[], ApiClientError>({ queryKey: ['pipeline-stages'], queryFn: () => get('/pipeline/stages'), enabled: !!id });
  const options = useQuery<DealOptions, ApiClientError>({ queryKey: ['deal-options'], queryFn: () => get('/deals/options'), enabled: !!id });
  const deal = detail.data;

  useEffect(() => {
    if (!deal) return;
    setTitle(deal.title);
    setStageId(deal.stageId);
    setProbability(String(deal.probability));
    setExpectedCloseDate(deal.expectedCloseDate ?? '');
    setServiceKey(deal.serviceKey ?? '');
    setMarketCode(deal.marketCode ?? '');
    setConflict(null);
  }, [deal]);

  const save = useMutation<DealSummary, ApiClientError>({
    mutationFn: () => patch(`/deals/${id}`, {
      expectedVersion: deal!.version,
      ...(can('leads.pii.read') ? { title: title.trim() } : {}),
      stageId,
      probability: Number(probability),
      expectedCloseDate: expectedCloseDate || null,
      serviceKey: serviceKey || null,
      marketCode: marketCode || null,
    }),
    onSuccess: (fresh) => {
      qc.setQueryData(['deal', id], fresh);
      void qc.invalidateQueries({ queryKey: ['deals'] });
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
      toast({ tone: 'success', message: 'Deal saved.' });
    },
    onError: (error) => {
      if (error.code === 'VERSION_CONFLICT') setConflict(error.message);
      else toast({ tone: 'error', message: error.message });
    },
  });
  const canSave = !!deal && !!stages.data && !!options.data && stageId && probability !== '' && Number.isInteger(Number(probability)) && Number(probability) >= 0 && Number(probability) <= 100 && (!can('leads.pii.read') || !!title.trim());
  const availableStages = stages.data?.filter((stage) => stage.active || stage.id === deal?.stageId) ?? [];

  return (
    <Drawer open={!!id} onClose={onClose} title="Deal" width={520}>
      {detail.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading deal…</span><Skeleton height={170} /></div> : detail.isError ? <QueryError error={detail.error} onRetry={() => void detail.refetch()} /> : deal ? (
        <>
          <div className="drawer-id"><h3>{deal.title}</h3><div className="drawer-id__row"><Badge tone={deal.status === 'WON' ? 'success' : deal.status === 'LOST' ? 'neutral' : 'accent'}>{deal.status === 'PROPOSAL' ? deal.stageName : deal.status === 'WON' ? 'Won' : 'Lost'}</Badge><span>{deal.amount} {deal.currency}</span></div></div>
          <dl className="facts">
            <dt>Owner</dt><dd>{deal.ownerName ?? 'Unassigned'}</dd>
            <dt>Proposed</dt><dd>{fmtDate(deal.proposedAt)}</dd>
            {deal.wonAt ? <><dt>Won</dt><dd>{fmtDate(deal.wonAt)}</dd></> : null}
            <dt>Source</dt><dd>{deal.landingName} · v{deal.landingVersionNumber}</dd>
            {can('leads.read') ? <><dt>Lead</dt><dd><Link to={`/leads?lead=${deal.leadId}`} onClick={onClose}>Open lead and outcome controls</Link></dd></> : null}
          </dl>
          {deal.status !== 'PROPOSAL' ? <p className="ag-muted">This deal is closed. Changes to its value or outcome need an explicit adjustment workflow.</p> : can('deals.edit') ? (
            <div className="formgrid">
              {conflict ? <ConflictNotice message={conflict} onReload={() => { setConflict(null); void detail.refetch(); }} /> : null}
              {stages.isError ? <QueryError error={stages.error} onRetry={() => void stages.refetch()} /> : null}
              {options.isError ? <QueryError error={options.error} onRetry={() => void options.refetch()} /> : null}
              {can('leads.pii.read') ? <TextField label="Deal title" value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} /> : null}
              <SelectField label="Open stage" value={stageId} onChange={(event) => setStageId(event.target.value)} disabled={!stages.data}>
                {availableStages.map((stage) => <option key={stage.id} value={stage.id}>{stage.name}{stage.active ? '' : ' (inactive)'}</option>)}
              </SelectField>
              <TextField label="Probability (%)" type="number" min={0} max={100} step={1} value={probability} onChange={(event) => setProbability(event.target.value)} />
              <TextField label="Expected close date" type="date" value={expectedCloseDate} onChange={(event) => setExpectedCloseDate(event.target.value)} />
              <SelectField label="Service" value={serviceKey} onChange={(event) => setServiceKey(event.target.value)} disabled={!options.data}>
                <option value="">Not set</option>
                {deal.serviceKey && !options.data?.services.some((service) => service.key === deal.serviceKey) ? <option value={deal.serviceKey}>{deal.serviceKey} (unavailable)</option> : null}
                {options.data?.services.map((service) => <option key={service.key} value={service.key}>{service.name}</option>)}
              </SelectField>
              <SelectField label="Market" value={marketCode} onChange={(event) => setMarketCode(event.target.value)} disabled={!options.data}>
                <option value="">Not set</option>
                {deal.marketCode && !options.data?.markets.some((market) => market.code === deal.marketCode) ? <option value={deal.marketCode}>{deal.marketCode} (unavailable)</option> : null}
                {options.data?.markets.map((market) => <option key={market.code} value={market.code}>{market.name}</option>)}
              </SelectField>
              <Button variant="primary" loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>Save deal</Button>
            </div>
          ) : null}
          <RecordWorkPanel target={{ targetType: 'DEAL', targetId: deal.id }} />
        </>
      ) : null}
    </Drawer>
  );
}
